import { VStack, HStack, CircularProgress, Skeleton, Box } from '@chakra-ui/react';
import { Typography } from 'Tokens';
import { CompanyBottomUpIcon, CompanyTopDownIcon } from 'Tokens/Icons/Custom';
import { useNavigate, useParams } from 'react-router-dom';
import { Tag, Tooltip } from 'Atoms';
import { MaterialStandard } from '../../GroupAssessment/GroupAssessmentOverview.hooks';
import { WarningIcon } from 'Tokens/Icons/Status';
import { useState, useEffect, useMemo } from 'react';
import { FrameworkRefs } from '..';
import { useNhostClient } from 'Providers/nhost/NhostProvider.tsx';

export type ProgressPayload = Record<string, any>;

const useProgress = (requestUrl: string, payload: ProgressPayload, dependencies: any[]) => {
  const nhost = useNhostClient();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('0');

  const getProgress = async () => {
    setLoading(true);
    try {
      const res = await nhost.functions.post<{ progress: string }>(requestUrl, payload);

      const progressValue = res.body?.progress;
      if (progressValue) {
        setProgress(progressValue);
      }
    } catch (error) {
      console.error('Error fetching progress:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getProgress();
  }, dependencies);

  return { loading, progress };
};

export const useStandardProgress = (payload: ProgressPayload, dependencies: any[]) => {
  return useProgress('/esrs/progress/standard-progress', payload, dependencies);
};

export const useAssessmentProgress = (payload: ProgressPayload, dependencies: any[]) => {
  return useProgress('/esrs/progress/company-progress', payload, dependencies);
};

export const StandardProgressCard = ({
  standard,
  standardIndex,
  categoryIndex,
  standardLength,
  isFlagged,
  navigateTo,
  icon,
  iconToolTipLabel,
  loading,
  progress,
}: {
  standard: MaterialStandard;
  standardIndex: number;
  categoryIndex: number;
  standardLength: number;
  isFlagged: boolean;
  navigateTo: () => void;
  icon?: React.ReactNode;
  iconToolTipLabel: string;
  loading: boolean;
  progress: string;
}) => {
  const { framework = '' } = useParams();
  return (
    <VStack
      shadow="interactive"
      bg="bg.elevated.interactive"
      borderRadius="10px"
      p="20px"
      gap="8px"
      alignItems="start"
      minHeight="148px"
      _hover={{
        shadow: 'interactive.hover',
        background: 'bg.elevated.interactive.hover',
      }}
      cursor="pointer"
      key={standard.standardRef}
      onClick={navigateTo}
      justifyContent="space-between"
      {...(categoryIndex === 0 && (standardLength > 1 ? standardIndex === 1 : standardIndex === 0)
        ? { className: 'walktour-standard-overview' }
        : {})}
    >
      <HStack alignItems="start" justifyContent="space-between" width="100%">
        <VStack spacing="4px" alignItems="start">
          <Typography variant="h4">{standard.standardName}</Typography>
          {framework !== FrameworkRefs.vsme && (
            <Typography variant="detail" color="text.hint">
              {standard.standardRef}
            </Typography>
          )}
          {framework !== standard.frameworkRef && (
            <Typography variant="body" color={'text.hint'}>
              {standard.frameworkRef}
            </Typography>
          )}
        </VStack>
        {isFlagged && (
          <Box>
            <Tooltip label="Please review and update targets baseline and milestones values to make sure a correct reporting.">
              <Tag variant="warning" size="xs">
                <WarningIcon color="text.warning" />
              </Tag>
            </Tooltip>
          </Box>
        )}
      </HStack>
      <HStack spacing="8px">
        {icon && (
          <Tag variant="undefined" size="xs">
            <Tooltip label={iconToolTipLabel}>{icon}</Tooltip>
          </Tag>
        )}
        <Skeleton isLoaded={!loading}>
          <HStack spacing="6px">
            <CircularProgress size="16px" color="text.info" value={Number(progress ?? '0')} />
            <Typography variant="body">{`${progress}%`}</Typography>
          </HStack>
        </Skeleton>
      </HStack>
    </VStack>
  );
};

export const StandardProgress = ({
  standard,
  standardIndex,
  categoryIndex,
  standardLength,
  isFlagged,
  drRefs,
  parentAssessmentId,
}: {
  standard: MaterialStandard;
  standardIndex: number;
  categoryIndex: number;
  standardLength: number;
  isFlagged: boolean;
  drRefs: string[];
  parentAssessmentId: string | undefined;
}) => {
  const { esrsAssessmentId, framework = '' } = useParams();
  const navigate = useNavigate();

  const payload = useMemo(
    () => ({
      drRefs,
      assessmentId: esrsAssessmentId,
      standardRef: standard.standardRef,
      parentAssessmentId,
      type: 'company',
      frameworkRef: framework,
    }),
    [drRefs, esrsAssessmentId, standard.standardRef, parentAssessmentId, framework]
  );

  const { loading, progress } = useStandardProgress(payload, [
    standard,
    esrsAssessmentId,
    parentAssessmentId,
  ]);

  const navigateTo = () => {
    navigate(`standard/${standard.standardRef}`);
  };

  return (
    <StandardProgressCard
      standard={standard}
      standardIndex={standardIndex}
      categoryIndex={categoryIndex}
      standardLength={standardLength}
      isFlagged={isFlagged}
      navigateTo={navigateTo}
      icon={
        standard.isMaterialForGroup && !standard.isMaterialForSubsidiary ? (
          <CompanyTopDownIcon boxSize="16px" />
        ) : null
      }
      iconToolTipLabel={'Assessed as material by a parent company'}
      loading={loading}
      progress={progress}
    />
  );
};

export const ReportingUnitStandardProgress = ({
  standard,
  standardIndex,
  categoryIndex,
  reportingUnitId,
  drRefs,
  parentAssessmentId,
}: {
  standard: MaterialStandard;
  standardIndex: number;
  categoryIndex: number;
  reportingUnitId: string;
  drRefs: string[];
  parentAssessmentId: string | undefined;
}) => {
  const navigate = useNavigate();
  const { esrsAssessmentId, framework = '' } = useParams();

  const payload = useMemo(
    () => ({
      drRefs,
      assessmentId: esrsAssessmentId,
      standardRef: standard.standardRef,
      parentAssessmentId,
      reportingUnitId,
      type: 'reportingUnit',
      frameworkRef: framework,
    }),
    [drRefs, esrsAssessmentId, standard.standardRef, parentAssessmentId, reportingUnitId, framework]
  );

  const { loading, progress } = useStandardProgress(payload, [
    drRefs,
    esrsAssessmentId,
    standard,
    parentAssessmentId,
    reportingUnitId,
  ]);

  const navigateTo = () => {
    navigate(`bu-standard/${standard.standardRef}/bu/${reportingUnitId}`);
  };

  return (
    <StandardProgressCard
      standard={standard}
      standardIndex={standardIndex}
      categoryIndex={categoryIndex}
      standardLength={0}
      isFlagged={false}
      navigateTo={navigateTo}
      icon={
        standard.isMaterialForGroup ? (
          <CompanyTopDownIcon boxSize="16px" />
        ) : (
          <CompanyBottomUpIcon boxSize="16px" />
        )
      }
      iconToolTipLabel={
        standard.isMaterialForGroup
          ? 'Assessed as material by a parent company'
          : 'Assessed as material by this company'
      }
      loading={loading}
      progress={progress}
    />
  );
};

export const GroupStandardProgress = ({
  standard,
  standardIndex,
  categoryIndex,
  drRefs,
}: {
  standard: MaterialStandard;
  standardIndex: number;
  categoryIndex: number;
  drRefs: string[];
}) => {
  const navigate = useNavigate();
  const { esrsAssessmentId, framework = '' } = useParams();

  const payload = useMemo(
    () => ({
      drRefs,
      assessmentId: esrsAssessmentId,
      standardRef: standard.standardRef,
      type: 'group',
      frameworkRef: framework,
    }),
    [drRefs, esrsAssessmentId, standard.standardRef, framework]
  );

  const { loading, progress } = useStandardProgress(payload, [drRefs, esrsAssessmentId, standard]);

  const navigateTo = () => {
    navigate(`standard/${standard.standardRef}`);
  };

  return (
    <StandardProgressCard
      standard={standard}
      standardIndex={standardIndex}
      categoryIndex={categoryIndex}
      standardLength={0}
      isFlagged={false}
      navigateTo={navigateTo}
      icon={
        !standard.isMaterialForGroup && standard.isMaterialForSubsidiary ? (
          <CompanyBottomUpIcon boxSize="16px" />
        ) : null
      }
      iconToolTipLabel={'Not material for the group, but material for one or several subsidiaries'}
      loading={loading}
      progress={progress}
    />
  );
};
export const AssessmentProgress = ({
  isGroup,
  parentAssessmentId,
}: {
  isGroup: boolean;
  parentAssessmentId?: string;
}) => {
  const { esrsAssessmentId = '', framework = '' } = useParams();

  const payload = useMemo(
    () => ({
      assessmentId: esrsAssessmentId,
      type: isGroup ? 'group' : 'company',
      frameworkRef: framework,
      parentAssessmentId,
    }),
    [esrsAssessmentId, isGroup, framework, parentAssessmentId]
  );

  const { loading, progress } = useAssessmentProgress(payload, [
    isGroup,
    esrsAssessmentId,
    framework,
    parentAssessmentId,
  ]);

  return (
    <Skeleton isLoaded={!loading}>
      <HStack spacing="8px">
        <CircularProgress
          size="16px"
          thickness="12px"
          color="text.info"
          value={Number(progress ?? '0')}
        />
        <Typography variant="h3">{`${progress}%`}</Typography>
      </HStack>
    </Skeleton>
  );
};
